/**
 * The default face of a device sync: a compact card in the toast corner.
 *
 * It carries just enough to judge the run at a glance — who is being synced, how far along
 * it is, and how many devices took it — and auto-dismisses like any other toast (the timer
 * lives in SyncProgressProvider). "Ampliar" promotes it to pages/SyncProgress, which has
 * the per-device list, the failure reasons, pause/stop and retry.
 */

import React, { useState } from 'react';
import { CheckCircle, XCircle, RefreshCw, Maximize2, X, User } from 'lucide-react';
import { SyncProgressState } from './SyncProgressModal';
import { syncHeadline } from '../utils/syncProgressPresenter';
import { getAbsoluteImageUrl } from '../utils/imageUrl';

interface SyncProgressCardProps {
  data: SyncProgressState;
  onExpand: () => void;
  onClose: () => void;
  /** react-hot-toast's enter/leave flag; drives the fade so dismissal is not a snap. */
  visible?: boolean;
  /**
   * Passed in rather than read from context: the card renders inside the Toaster, which
   * lives above OperationalSettingsProvider in the tree and cannot see it.
   */
  isDark?: boolean;
}

export function SyncProgressCard({ data, onExpand, onClose, visible = true, isDark = false }: SyncProgressCardProps) {
  const [photoFailed, setPhotoFailed] = useState(false);

  const { zoneName, personPhotoUrl, total, done, ok, fail, isComplete } = data;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const pending = Math.max(0, total - done);
  const isRunning = !isComplete;
  const photoSrc = getAbsoluteImageUrl(personPhotoUrl);

  const panel = isDark
    ? 'bg-gray-900 text-gray-100 border-gray-700'
    : 'bg-white text-gray-900 border-gray-200';
  const muted = isDark ? 'text-gray-400' : 'text-gray-500';
  const iconBtn = isDark
    ? 'text-gray-400 hover:bg-gray-700'
    : 'text-gray-400 hover:bg-gray-100';

  return (
    <div
      className={`w-[320px] rounded-xl border shadow-lg overflow-hidden transition-opacity duration-200 ${panel}
        ${visible ? 'opacity-100' : 'opacity-0'}`}
    >
      <div className="flex items-center gap-2.5 px-3 pt-3">
        {photoSrc && !photoFailed ? (
          <img
            src={photoSrc}
            alt=""
            onError={() => setPhotoFailed(true)}
            className="w-6 h-8 rounded-md object-cover flex-shrink-0 ring-1 ring-black/10"
          />
        ) : (
          <div className="w-6 h-8 rounded-md bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0 ring-1 ring-black/10">
            <User className="w-3.5 h-3.5" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold leading-tight truncate">
            {syncHeadline({ isComplete, isStopped: false, ok, fail })}
          </p>
          <p className={`text-[11px] truncate ${muted}`}>{zoneName}</p>
        </div>

        <button
          onClick={onExpand}
          aria-label="Ampliar"
          title="Ver detalhes"
          className={`p-1.5 rounded-lg transition-colors ${iconBtn}`}
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onClose}
          aria-label="Fechar"
          className={`p-1.5 rounded-lg transition-colors ${iconBtn}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="px-3 pt-2 pb-3">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-[11px] tabular-nums ${muted}`}>{done}/{total}</span>
          <span className="text-[11px] font-semibold tabular-nums">{pct}%</span>
        </div>
        <div className={`w-full h-1 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${pct}%`, backgroundColor: fail > 0 ? '#f59e0b' : '#96bf13' }}
          />
        </div>

        <div className="flex items-center gap-3.5 mt-2">
          <span className="flex items-center gap-1" data-testid="sync-card-ok">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span className="text-[11px] font-semibold text-green-500 tabular-nums">{ok}</span>
          </span>
          <span className="flex items-center gap-1" data-testid="sync-card-fail">
            <XCircle className="w-3 h-3 text-red-400" />
            <span className="text-[11px] font-semibold text-red-400 tabular-nums">{fail}</span>
          </span>
          <span className="flex items-center gap-1" data-testid="sync-card-pending">
            <RefreshCw className={`w-3 h-3 ${muted} ${isRunning ? 'animate-spin' : ''}`} />
            <span className={`text-[11px] font-semibold tabular-nums ${muted}`}>{pending}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
